import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { KatexRenderer } from '@/components/shared/KatexRenderer'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import {
  Maximize2,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  ExternalLink,
  ImageOff,
  Loader2,
  Compass
} from 'lucide-react'

export interface QuestionContextData {
  id?: number
  external_id: string
  context_type: string
  title?: string | null
  direction_text: string
  body_text?: string | null
  media_url?: string | null
  context_image_url?: string | null
  media_type?: string | null
  chart_data?: Record<string, any>
}

interface QuestionContextCardProps {
  context: QuestionContextData
  format?: string
  className?: string
  compact?: boolean
  showMeta?: boolean
}

export function cleanContextTitle(title?: string | null): string {
  if (!title) return 'Directions'

  // Remove any parenthesized or bracketed hex hashes e.g. "(455acd4d7aa6)", "[455acd4d7aa6]", or standalone hex strings
  let cleaned = title.replace(/[\(\[]?[a-f0-9]{6,}[\)\]]?/gi, '').trim()
  cleaned = cleaned.replace(/^[\s\-_:]+|[\s\-_:]+$/g, '').trim()

  // Match scaffold patterns like "ENG Stimulus Context", "Stimulus Context", "Quant Context", etc.
  const isScaffold = /^(?:ENG|ENGLISH|QUANT|REASONING|MATH|LOGICAL|LRDI|DI|RC)?\s*stimulus(?:\s+context)?$/i.test(cleaned) ||
                     /^(?:stimulus|context|question\s*context)$/i.test(cleaned)

  if (isScaffold || !cleaned) {
    return 'Directions'
  }

  return cleaned
}

export function QuestionContextCard({
  context,
  format = 'markdown',
  className = '',
  compact: _compact = false,
  showMeta = false
}: QuestionContextCardProps) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [imageLoading, setImageLoading] = useState(true)
  const [imageError, setImageError] = useState(false)
  const [zoomLevel, setZoomLevel] = useState(1)

  const isRawHexHash = (val?: string | null) => Boolean(val && /^[a-f0-9]{6,}$/i.test(val.trim()))
  const hasMeta = Boolean(showMeta && context.external_id && !isRawHexHash(context.external_id))

  const effectiveMediaUrl = context.context_image_url || context.media_url
  const isAudio = context.media_type === 'audio' || context.context_type === 'audio'
  const isImageOrSvg = Boolean(effectiveMediaUrl && !isAudio)

  const handleZoomIn = () => setZoomLevel((prev) => Math.min(prev + 0.25, 3))
  const handleZoomOut = () => setZoomLevel((prev) => Math.max(prev - 0.25, 0.5))
  const handleResetZoom = () => setZoomLevel(1)

  const formatContextType = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'data_interpretation':
      case 'di':
        return 'Data Interpretation'
      case 'reading_comprehension':
      case 'rc':
        return 'Reading Comprehension'
      case 'puzzle':
        return 'Logical Puzzle'
      case 'caselet':
        return 'Caselet'
      case 'directions':
      case 'direction':
        return 'Directions'
      default:
        return type ? type.replace(/_/g, ' ').toUpperCase() : 'Directions'
    }
  }

  const hasBodyOrMedia = Boolean(
    (context.body_text && context.body_text.trim().length > 0) ||
    context.media_url ||
    context.context_image_url
  )

  const isDirectionsOnly = !hasBodyOrMedia && Boolean(
    (context.context_type || '').toLowerCase() === 'directions' ||
    Boolean(context.direction_text) ||
    /^(?:in\s+each\s+of\s+the\s+following|directions?[:\s\-]|refer\s+to\s+the\s+sentence|read\s+the\s+following\s+sentence|for\s+questions?\s+\d+|each\s+question\s+below)/i.test((context.direction_text || '').trim())
  )

  const isInternalScaffoldTitle = /stimulus\s+context|[a-f0-9]{6,}/i.test(context.title || '')
  const cleanedTitle = cleanContextTitle(context.title)
  const displayBadgeText = isDirectionsOnly
    ? 'Directions'
    : (context.context_type && !['directions', 'direction'].includes(context.context_type.toLowerCase())
        ? formatContextType(context.context_type)
        : 'Reading Comprehension')
  const displayTitle = isInternalScaffoldTitle
    ? (isDirectionsOnly ? 'Directions' : 'Reading Comprehension')
    : (cleanedTitle.toLowerCase() === 'directions' ? (isDirectionsOnly ? null : 'Directions') : cleanedTitle)

  return (
    <div className={`rounded-2xl border border-border/80 bg-card/60 backdrop-blur-xs overflow-hidden shadow-glass-xs ${className}`}>
      {/* Context Header (Clean, only shown if Title or Meta exists) */}
      {(Boolean(displayTitle) || hasMeta || isDirectionsOnly || context.context_type) && (
        <div className="px-4 py-2.5 sm:px-5 sm:py-3 bg-muted/20 border-b border-border/50 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="text-[10px] font-semibold bg-muted/60 text-muted-foreground border-border/60 rounded-md px-2 py-0.5">
              {displayBadgeText}
            </Badge>
            {displayTitle && displayTitle.toLowerCase() !== displayBadgeText.toLowerCase() && (
              <h3 className="font-semibold text-foreground text-xs sm:text-sm tracking-tight">
                {displayTitle}
              </h3>
            )}
          </div>
          {hasMeta && context.external_id && (
            <span className="text-[10px] font-mono text-muted-foreground bg-background/50 px-2 py-0.5 rounded border border-border/40 ml-auto">
              {context.external_id}
            </span>
          )}
        </div>
      )}

      {/* Direction & Content Body */}
      <div className="p-4 sm:p-5 space-y-3">
        {/* Direction Text Callout */}
        {context.direction_text && (
          <div className="rounded-xl border border-primary/20 bg-primary/5 p-3 sm:p-3.5 space-y-1">
            <div className="flex items-center gap-1.5 text-[10px] font-bold text-primary tracking-wider uppercase">
              <Compass className="h-3.5 w-3.5 text-primary shrink-0" />
              <span>Directions</span>
            </div>
            <div className="text-foreground/90 text-xs sm:text-[13px] leading-relaxed italic">
              <KatexRenderer
                content={context.direction_text}
                format={format}
                disableDirectionCallout={true}
              />
            </div>
          </div>
        )}

        {/* Media url (image/SVG or audio) */}
        {effectiveMediaUrl && (
          <div className="my-3">
            {isAudio ? (
              <div className="bg-muted/30 p-3 rounded-xl border border-border/50 flex justify-center">
                <audio controls className="w-full max-w-md my-1">
                  <source src={effectiveMediaUrl} />
                  Your browser does not support the audio element.
                </audio>
              </div>
            ) : isImageOrSvg ? (
              <div className="relative group rounded-2xl border border-border/70 bg-muted/20 p-2 sm:p-3 overflow-hidden shadow-xs">
                {/* Image error fallback */}
                {imageError ? (
                  <div className="w-full py-8 flex flex-col items-center justify-center gap-2 text-center bg-destructive/5 border border-destructive/20 rounded-xl p-4">
                    <ImageOff className="h-8 w-8 text-destructive/70" />
                    <p className="text-xs font-semibold text-foreground">Diagram asset could not be loaded</p>
                    <span className="text-[10px] text-muted-foreground font-mono truncate max-w-xs">{effectiveMediaUrl}</span>
                  </div>
                ) : (
                  <div
                    className="relative flex justify-center items-center cursor-zoom-in group/img min-h-[140px]"
                    onClick={() => setIsModalOpen(true)}
                  >
                    {/* Image loading skeleton overlay */}
                    {imageLoading && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-muted/40 animate-pulse rounded-xl z-10">
                        <Loader2 className="h-6 w-6 animate-spin text-primary/70" />
                        <span className="text-xs text-muted-foreground font-medium">Loading diagram...</span>
                      </div>
                    )}

                    <img
                      ref={(img) => {
                        if (img && img.complete && img.naturalHeight !== 0 && imageLoading) {
                          setImageLoading(false)
                        }
                      }}
                      src={effectiveMediaUrl}
                      alt={context.title || 'Question Context Diagram'}
                      className={`max-h-[380px] w-auto max-w-full object-contain rounded-xl transition-opacity duration-300 group-hover/img:scale-[1.01] ${
                        imageLoading ? 'opacity-0' : 'opacity-100'
                      }`}
                      loading="eager"
                      onLoad={() => setImageLoading(false)}
                      onError={() => {
                        setImageLoading(false)
                        setImageError(true)
                      }}
                    />

                    {/* Hover expand hint pill */}
                    {!imageLoading && (
                      <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-card/90 backdrop-blur-xs border border-border/80 shadow-glass-sm rounded-lg px-2.5 py-1 text-[11px] font-bold text-foreground flex items-center gap-1.5 pointer-events-none">
                        <Maximize2 className="h-3.5 w-3.5 text-primary" />
                        <span>Click to Expand Diagram</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )}

        {/* Body Text / Passage */}
        {context.body_text && (
          <div className="space-y-2 pt-1">
            {context.direction_text && (
              <div className="flex items-center gap-2 pt-1 pb-0.5">
                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                  {context.context_type === 'reading_comprehension' ? 'Passage' : 'Passage / Context'}
                </span>
                <div className="flex-1 h-px bg-border/60" />
              </div>
            )}
            <div className="text-foreground text-xs sm:text-sm leading-relaxed overflow-x-auto space-y-3 font-normal">
              <KatexRenderer content={context.body_text} format={format} />
            </div>
          </div>
        )}
      </div>

      {/* High-Resolution Diagram Modal View */}
      {isImageOrSvg && effectiveMediaUrl && !imageError && (
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-4xl max-h-[92vh] flex flex-col p-4 sm:p-6 bg-card border-border/90 shadow-glass-xl rounded-2xl">
            <DialogHeader className="flex flex-row items-center justify-between pb-3 border-b border-border/60 pr-8">
              <div>
                <DialogTitle className="text-base font-bold text-foreground">
                  {context.title || 'Context Diagram & Graph'}
                </DialogTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  High-resolution diagram inspection • Use controls to zoom in/out
                </p>
              </div>

              {/* Controls bar */}
              <div className="flex items-center gap-1 bg-muted/50 p-1 rounded-xl border border-border/60">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 rounded-lg text-muted-foreground hover:text-foreground hover:bg-card"
                  onClick={handleZoomOut}
                  title="Zoom Out"
                >
                  <ZoomOut className="h-3.5 w-3.5" />
                </Button>
                <span className="text-[11px] font-mono font-bold px-1.5 text-foreground">
                  {Math.round(zoomLevel * 100)}%
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 rounded-lg text-muted-foreground hover:text-foreground hover:bg-card"
                  onClick={handleZoomIn}
                  title="Zoom In"
                >
                  <ZoomIn className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 rounded-lg text-muted-foreground hover:text-foreground hover:bg-card"
                  onClick={handleResetZoom}
                  title="Reset Zoom"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                </Button>
                {effectiveMediaUrl && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 rounded-lg text-muted-foreground hover:text-foreground hover:bg-card"
                    asChild
                    title="Open Full Asset in New Tab"
                  >
                    <a href={effectiveMediaUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </Button>
                )}
              </div>
            </DialogHeader>

            {/* Scrollable Diagram Viewer Canvas */}
            <div className="flex-1 overflow-auto min-h-[300px] max-h-[68vh] p-4 flex items-center justify-center bg-muted/20 rounded-xl border border-border/40 my-2">
              <img
                src={effectiveMediaUrl}
                alt={context.title || 'Context High-Res Graph'}
                style={{ transform: `scale(${zoomLevel})`, transformOrigin: 'center center' }}
                className="max-w-full max-h-full object-contain transition-transform duration-150 rounded-lg"
              />
            </div>

            <div className="flex items-center justify-between text-xs text-muted-foreground pt-2">
              <span className="truncate max-w-sm font-mono text-[11px]">{effectiveMediaUrl}</span>
              <Button
                variant="outline"
                size="sm"
                className="rounded-xl border-border font-bold text-xs"
                onClick={() => setIsModalOpen(false)}
              >
                Close Diagram
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

